import { Box, Grid2 as Grid, Paper, TextField, Typography } from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import TargetRuleBuilder from './TargetRuleBuilder';
import {
  calculateAllocatedCount,
  isLevelOnlyRule,
  validateRule as validateRuleService,
} from '../services/validationRuleService';
import { TargetRule } from '../types';
import { usePositionService, PositionType } from 'domain/shared/employee';
import useQualificationStructureService from '../services/qualificationStructureService';
import { useMessageService, ErrorMessage } from 'framework/ui/Message';
import { Alert, Button } from 'framework/ui/mui-builder';
import React, { useEffect, useState } from 'react';

import { useTechnologyService, type Technology } from 'domain/shared/career';
import ViewQualificationStructureBuilderBreadcrumbs from '../breadcrumbs/QualificationStructureBuilderBreadcrumbs';

const QualificationStructureBuilder = () => {
  const [totalHeadcount, setTotalHeadcount] = useState<number>(0);
  const [rules, setRules] = useState<TargetRule[]>([]);
  const [positions, setSpecializations] = useState<PositionType[]>([]);
  const [technologies, setTechnologies] = useState<Technology[]>([]);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const { addMessage } = useMessageService();

  const { findAll: findAllPositions } = usePositionService();
  const { findAll: findAllTechnologies } = useTechnologyService();
  const { submitQualificationStructure } = useQualificationStructureService();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [positionsData, technologiesData] = await Promise.all([findAllPositions(), findAllTechnologies()]);

        setSpecializations(positionsData as PositionType[]);
        setTechnologies(technologiesData as Technology[]);
      } catch (err) {
        addMessage(new ErrorMessage('Failed to load specializations and technologies. Please try again later.'));

        setSpecializations([]);
        setTechnologies([]);
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const validateRule = (newRule: Omit<TargetRule, 'id'>, editingId?: string) => {
    return validateRuleService(rules, totalHeadcount, positions, newRule, technologies, editingId);
  };

  const handleAddRule = (newRule: Omit<TargetRule, 'id'>) => {
    const existingRuleIndex = rules.findIndex(
      rule =>
        rule.competencyLevel === newRule.competencyLevel &&
        rule.positionId === newRule.positionId &&
        rule.technologyId === newRule.technologyId &&
        !(isLevelOnlyRule(rule) && isLevelOnlyRule(newRule)),
    );

    if (existingRuleIndex >= 0 && !isLevelOnlyRule(newRule)) {
      const updatedRules = [...rules];
      updatedRules[existingRuleIndex] = {
        ...updatedRules[existingRuleIndex],
        capacity: updatedRules[existingRuleIndex].capacity + newRule.capacity,
      };
      setRules(updatedRules);
    } else {
      const rule: TargetRule = {
        ...newRule,
        id: `rule-${Date.now()}`,
      };
      setRules([...rules, rule]);
    }
  };

  const handleEditRule = (id: string, updatedRule: Omit<TargetRule, 'id'>) => {
    const updatedRules = rules.map(rule => (rule.id === id ? { ...updatedRule, id } : rule));
    setRules(updatedRules);
  };

  const handleDeleteRule = (id: string) => {
    setRules(rules.filter(rule => rule.id !== id));
  };

  const allocatedCount = calculateAllocatedCount(rules);
  const remainingCount = totalHeadcount - allocatedCount;

  const handleSubmit = async () => {
    if (totalHeadcount <= 0) {
      addMessage(new ErrorMessage('Please set a total headcount value'));
      return;
    }

    setIsSubmitting(true);
    try {
      await submitQualificationStructure(totalHeadcount, rules, positions, technologies);
      setTotalHeadcount(0);
      setRules([]);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box>
      <Box sx={{ pl: 3, gap: 3, display: 'flex', flexDirection: 'column' }}>
        <ViewQualificationStructureBuilderBreadcrumbs />
        <Typography variant="h1" mb={3} color="brand">
          Qualification Structure Builder
        </Typography>
      </Box>
      <Box sx={{ p: 3 }}>
        <Paper sx={{ p: 2, mb: 3 }}>
          <Grid container spacing={2} alignItems="start">
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                hiddenLabel
                placeholder="Total Headcount"
                type="number"
                fullWidth
                value={totalHeadcount}
                slotProps={{
                  htmlInput: { min: 1 },
                }}
                onChange={e => setTotalHeadcount(Number(e.target.value) || 0)}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Box
                sx={{ display: 'flex', alignItems: 'start', justifyContent: 'flex-end', height: '100%' }}
                data-testid="allocation-status"
              >
                <Typography variant="body1">
                  <strong>Allocated:</strong> {allocatedCount} / {totalHeadcount}
                  {' | '}
                  <strong>Remaining:</strong> {remainingCount}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {totalHeadcount > 0 ? (
          <>
            <TargetRuleBuilder
              rules={rules}
              onAddRule={handleAddRule}
              onDeleteRule={handleDeleteRule}
              onEditRule={handleEditRule}
              remainingCount={remainingCount}
              validateRule={validateRule}
              positions={positions}
              technologies={technologies}
            />
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mb: 2 }}>
              {rules.length > 0 && (
                <Button startIcon={<SendIcon />} onClick={handleSubmit} disabled={isSubmitting || rules.length === 0}>
                  {isSubmitting ? 'Submitting...' : 'Submit qualification structure'}
                </Button>
              )}
            </Box>
          </>
        ) : (
          <Alert severity="info">Please set a total headcount value to start defining qualification structure.</Alert>
        )}
      </Box>
    </Box>
  );
};

export default QualificationStructureBuilder;
